
/** Digital wallets (Google Pay / Apple Pay) offered on the checkout page. All false by default. */
export type DigitalWalletOptions = {
	googlePay:boolean,
	googlePayForDelivery:boolean,
	applePay:boolean,
	applePayForDelivery:boolean
}

/** Available payment options for the restaurant */
export class PaymentOptions {
	static readonly DEFAULT_PAY_ONLINE_FEE 			 = 2.9;
	private static readonly DEFAULT_PICKUP_PAYMENT_OPTIONS 	 = ["Pay at the restaurant"];
	private static readonly DEFAULT_DELIVERY_PAYMENT_OPTIONS = ["Pay at the door"];

	payAtRestaurant:boolean;
	payAtRestaurantForDelivery:boolean;
	payOnline: boolean;
	payOnlineForDelivery:boolean;
	payOnlineFee:number;
	payAtRestaurantOptionsPickup:string[];
	payAtRestaurantOptionsDelivery:string[];

	/** @deprecated Use the "thirdParty" variables below */ payMoneris:boolean;
	/** @deprecated Use the "thirdParty" variables below */ payMonerisForDelivery:boolean;
	/** @deprecated Use the "thirdParty" variables below */ monerisPostParams:any|null;

	showTipsOptionForPickup:boolean;
	showTipsOptionForDelivery:boolean;

	payThirdParty:boolean;
	payThirdPartyForDelivery:boolean;
	thirdPartyPostParams:any|null;
	thirdPartyType:string|null;

	digitalWallets:DigitalWalletOptions;

	/**
	 * Constructor
	 * @param payAtRestaurant If true, the user will be able to pay at the STORE for pickup
	 * @param payAtRestaurantForDelivery  If true, the user will be able to pay at the STORE for delivery
	 * @param payOnline If true, the user will be able to pay online, using sending money to a SQUARE account, for pickup
	 * @param payOnlineForDelivery If true, the user will be able to pay online, using sending money to a SQUARE account, for delivery
	 * @param payOnlineFee SQUARE payments (payment gateway we are using) has a credit card fee for every transaction. This variable
	 *  holds the % applied to the total to calculate this fee.
	 * Example: A $100 order would have a $2.90 fee if payOnlineFee is 2.9 (which is 2.9% of the total)
	 * @param payAtRestaurantOptionsPickup Text that is shown to the user for pickup, when he pays at the restaurant. I can have multiple strings. 
	 * 	Example: "pay with visa", "pay with cash", "pay with debit".... each string will be a different button on the checkout page.
	 * @param payAtRestaurantOptionsDelivery Same thing as payAtRestaurantOptionsPickup, but for delivery
	 * 
	 * @param payMoneris If true, the user will be able to use MONERIS as a third party payment page to pay for his food, for PICKUP.
	 * @param payMonerisForDelivery If true, the user will be able to use MONERIS as third party payment page to pay for his food, for PICKUP.
	 * @param monerisPostParams If "payMoneris" or "payMonerisForDelivery" is true, use this variable to inform the POST parameters required
	 *  for this payment gateway. We are going to use the "HOSTED PAYMENT PAGE". 
	 *  For more info, read: https://developer.moneris.com/Documentation/NA/E-Commerce%20Solutions/Hosted%20Solutions/Hosted%20Payment%20Page
	 * 
	 * @param payThirdParty If true, the user will be able to a third party payment page to pay for his food, for PICKUP.
	 * @param payThirdPartyForDelivery If true, the user will be able to a third party payment page to pay for his food, for DELIVERY.
	 * @param thirdPartyPostParams If "payThirdParty" or "payThirdPartyForDelivery" is true, use this variable to inform the POST parameters required
	 *  for this payment gateway.
	 * @param thirdPartyType the type of the payment provider used (for example: paypal, square, stripe...)
	 * @param digitalWallets Google Pay / Apple Pay switches for the checkout page. Missing values are treated as false.
	 */
	constructor(payAtRestaurant:boolean, payAtRestaurantForDelivery:boolean, payOnline: boolean, payOnlineForDelivery:boolean, payOnlineFee:number,
				payAtRestaurantOptionsPickup:string[], payAtRestaurantOptionsDelivery:string[],
				payMoneris:boolean, payMonerisForDelivery:boolean, monerisPostParams:any|null, showTipsOptionForPickup:boolean, showTipsOptionForDelivery:boolean,
				payThirdParty:boolean, payThirdPartyForDelivery:boolean, thirdPartyPostParams:any|null, thirdPartyType:string|null,
				digitalWallets:any|null = null) {
		this.payAtRestaurant = payAtRestaurant;
		this.payAtRestaurantForDelivery = payAtRestaurantForDelivery;
		this.payOnline = payOnline;
		this.payOnlineForDelivery = payOnlineForDelivery;
		this.payOnlineFee = payOnlineFee;
		this.payAtRestaurantOptionsPickup = payAtRestaurantOptionsPickup;
		this.payAtRestaurantOptionsDelivery = payAtRestaurantOptionsDelivery;
		this.payMoneris = payMoneris;
		this.payMonerisForDelivery = payMonerisForDelivery;
		this.monerisPostParams = monerisPostParams;
		this.showTipsOptionForPickup = showTipsOptionForPickup;
		this.showTipsOptionForDelivery = showTipsOptionForDelivery;
		this.payThirdParty = payThirdParty;
		this.payThirdPartyForDelivery = payThirdPartyForDelivery;
		this.thirdPartyPostParams = thirdPartyPostParams;
		this.thirdPartyType = thirdPartyType;
		this.digitalWallets = PaymentOptions.parseDigitalWallets(digitalWallets);
	}

	/** Returns the wallet switches with every missing or non-boolean value set to false */
	static parseDigitalWallets(awsDigitalWallets:any):DigitalWalletOptions{
		const wallets = awsDigitalWallets == null ? {} : awsDigitalWallets;
		return {
			googlePay:			  wallets.googlePay === true,
			googlePayForDelivery: wallets.googlePayForDelivery === true,
			applePay:			  wallets.applePay === true,
			applePayForDelivery:  wallets.applePayForDelivery === true
		};
	}

	/** Converts this object into a format that can be saved on our database (DynamoDB) */
	toAWSObject(){
		let payObj = {
			"payAtRestaurant":this.payAtRestaurant,
			"payAtRestaurantForDelivery":this.payAtRestaurantForDelivery,
			"payOnline":this.payOnline,
			"payOnlineForDelivery":this.payOnlineForDelivery,
			"payOnlineFee":this.payOnlineFee,
			"payAtRestaurantOptionsPickup":this.payAtRestaurantOptionsPickup,
			"payAtRestaurantOptionsDelivery":this.payAtRestaurantOptionsDelivery,

			"payMoneris":this.payMoneris,
			"payMonerisForDelivery":this.payMonerisForDelivery,
			"monerisPostParams":this.monerisPostParams,

			"showTipsOptionForPickup":this.showTipsOptionForPickup,
			"showTipsOptionForDelivery":this.showTipsOptionForDelivery,

			"payThirdParty":this.payThirdParty,
			"payThirdPartyForDelivery":this.payThirdPartyForDelivery,
			"thirdPartyPostParams":this.thirdPartyPostParams,
			"thirdPartyType":this.thirdPartyType,
			"digitalWallets":this.digitalWallets
		};
		return payObj;
	}

	/**
	 * Creates an Object of this class...
	 * by default: the user can pay at the restaurant for pickup ("PAY AT RESTAURANT" option). For delivery, the customer will only be able to pay online.
	 */
	static generateDefaultPaymentOptions(){
		return new PaymentOptions(
			true, false,
			false, true, this.DEFAULT_PAY_ONLINE_FEE, 
			PaymentOptions.DEFAULT_PICKUP_PAYMENT_OPTIONS, PaymentOptions.DEFAULT_DELIVERY_PAYMENT_OPTIONS,
			false, false, null, true, true, false, false, null, null);
	}

	static newFromAws(awsStatusItem:any):PaymentOptions {
		if(awsStatusItem!=null){
			const payAtRestaurant				 = awsStatusItem.payAtRestaurant==null?false:awsStatusItem.payAtRestaurant;
			const payAtRestaurantForDelivery	 = awsStatusItem.payAtRestaurantForDelivery==null?false:awsStatusItem.payAtRestaurantForDelivery;
			const payOnline 	  				 = awsStatusItem.payOnline==null?false:awsStatusItem.payOnline;
			const payOnlineForDelivery			 = awsStatusItem.payOnlineForDelivery==null?false:awsStatusItem.payOnlineForDelivery;
			const payOnlineFee					 = awsStatusItem.payOnlineFee==null?this.DEFAULT_PAY_ONLINE_FEE:awsStatusItem.payOnlineFee;
			const payAtRestaurantOptionsPickup 	 = awsStatusItem.payAtRestaurantOptionsPickup==null?PaymentOptions.DEFAULT_PICKUP_PAYMENT_OPTIONS:awsStatusItem.payAtRestaurantOptionsPickup;
			const payAtRestaurantOptionsDelivery = awsStatusItem.payAtRestaurantOptionsDelivery==null?PaymentOptions.DEFAULT_DELIVERY_PAYMENT_OPTIONS:awsStatusItem.payAtRestaurantOptionsDelivery;
			const payMoneris			 		 = awsStatusItem.payMoneris==null?false:awsStatusItem.payMoneris;
			const payMonerisForDelivery	 		 = awsStatusItem.payMonerisForDelivery==null?false:awsStatusItem.payMonerisForDelivery;
			const monerisPostParams			 	 = awsStatusItem.monerisPostParams;
			const tipsForPickup 				 = awsStatusItem.showTipsOptionForPickup==null?true:awsStatusItem.showTipsOptionForPickup;
			const tipsForDelivery				 = awsStatusItem.showTipsOptionForDelivery==null?true:awsStatusItem.showTipsOptionForDelivery;
			const payThirdParty					 = awsStatusItem.payThirdParty==null?false:awsStatusItem.payThirdParty;
			const payThirdPartyForDelivery		 = awsStatusItem.payThirdPartyForDelivery==null?false:awsStatusItem.payThirdPartyForDelivery;
			const thirdPartyPostParams			 = awsStatusItem.thirdPartyPostParams;
			const thirdPartyType				 = awsStatusItem.thirdPartyType;
			const digitalWallets				 = awsStatusItem.digitalWallets;


			return new PaymentOptions(
				payAtRestaurant, payAtRestaurantForDelivery,
				payOnline, payOnlineForDelivery, payOnlineFee,
				payAtRestaurantOptionsPickup, payAtRestaurantOptionsDelivery,
				payMoneris, payMonerisForDelivery, monerisPostParams, tipsForPickup, tipsForDelivery,
				payThirdParty, payThirdPartyForDelivery, thirdPartyPostParams, thirdPartyType,
				digitalWallets
			);
		}else{
			return PaymentOptions.generateDefaultPaymentOptions();
		}
	}
}